import React, { useState, useEffect } from 'react';
import { CheckCircle, HourglassMedium, PaperPlaneTilt, CaretRight, FilePdf, ArrowClockwise } from '@phosphor-icons/react';
import { api } from '../api';

/**
 * My Statement — what the contractor earned and was paid this financial year.
 *
 * Earnings come from the app; payments come from Zoho Books, where every
 * weekly bank transfer is already recorded. The two are matched on the week
 * number the bank description carries ("…NetBank WEEK 31"), so nobody has to
 * key payments in twice.
 */

const NAVY  = '#16303f';
const TEAL  = '#4A7A91';
const RED   = '#cc0000';
const GREEN = '#16a34a';

const lbl = { fontSize: 10.5, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: '#9ca3af' };
const card = { background: '#fff', padding: '14px 16px' };
const num = { fontVariantNumeric: 'tabular-nums' };

const fyBtn = {
  width: 44, height: 44, flex: 'none', background: '#fff', border: 'none', borderRadius: 12,
  boxShadow: '0 1px 3px rgba(0,0,0,.06)', fontSize: 20, color: NAVY, cursor: 'pointer', lineHeight: 1,
};

const STATE = {
  paid:             { label: 'Paid',                Icon: CheckCircle,      bg: '#dcfce7', fg: '#15803d' },
  awaiting_payment: { label: 'Awaiting payment',    Icon: HourglassMedium,  bg: '#fef6da', fg: '#a16207', pillFg: '#8a6410' },
  sent:             { label: 'Sent · not approved', Icon: PaperPlaneTilt,   bg: '#e8f2f7', fg: '#1e3a4a' },
  not_invoiced:     { label: 'Not invoiced yet',    Icon: PaperPlaneTilt,   bg: '#f3f4f6', fg: '#6b7280' },
};

const money = n => `$${Number(n || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function fmtDay(iso) {
  if (!iso) return '—';
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

function weekRange(from, to) {
  const a = new Date(from + 'T12:00:00');
  const b = new Date(to + 'T12:00:00');
  const mon = d => d.toLocaleDateString('en-AU', { month: 'short' });
  return a.getMonth() === b.getMonth()
    ? `${a.getDate()}–${b.getDate()} ${mon(b)}`
    : `${a.getDate()} ${mon(a)} – ${b.getDate()} ${mon(b)}`;
}

function monthKey(iso) {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
}

const MONTHS_PER_PAGE = 3;

export default function StatementMobile() {
  const [fy, setFy]           = useState(null);
  const [years, setYears]     = useState([]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [filter, setFilter]   = useState('All');
  const [monthsShown, setMonthsShown] = useState(MONTHS_PER_PAGE);
  const [sheet, setSheet]     = useState(null);   // the week whose payment is open

  useEffect(() => {
    api.getMyStatementYears()
      .then(r => { setYears(r.years || []); setFy(r.current); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  useEffect(() => {
    if (fy == null) return;
    setLoading(true); setError(''); setMonthsShown(MONTHS_PER_PAGE);
    api.getMyStatement(fy)
      .then(setData)
      .catch(e => { setError(e.message); setData(null); })
      .finally(() => setLoading(false));
  }, [fy]);

  const weeks = data?.weeks || [];
  const shown = weeks.filter(w =>
    filter === 'All' ? true
    : filter === 'Paid' ? w.state === 'paid'
    : w.state !== 'paid');

  // Newest month first, weeks descending inside it — the rows already arrive
  // sorted, so grouping in order preserves both.
  const groups = [];
  shown.forEach(w => {
    const k = monthKey(w.week_start);
    const g = groups.find(x => x.key === k);
    (g || groups[groups.push({ key: k, rows: [] }) - 1]).rows.push(w);
  });
  const visibleGroups = groups.slice(0, monthsShown);

  const outstandingWeeks = weeks.filter(w => w.outstanding > 0.005);
  const canPrev = years.length && fy > Math.min(...years);
  const canNext = years.length && fy < Math.max(...years);

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', background: '#e7ecef', minHeight: '60vh' }}>

      {/* ── Intro block ───────────────────────────────────────────────── */}
      <div style={{ background: '#dce5eb', padding: '14px 16px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.3, color: '#6e8899', textTransform: 'uppercase', marginBottom: 4 }}>
          {data ? `${data.label} · Jul – Jun` : 'Financial year'}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 28, fontWeight: 800, color: NAVY, letterSpacing: -0.6, lineHeight: 1.05 }}>My Statement</span>
          <span style={{ fontSize: 13, fontWeight: 700, borderRadius: 22, padding: '6px 14px', background: '#fff', color: '#6e8899', whiteSpace: 'nowrap' }}>
            {weeks.length} week{weeks.length === 1 ? '' : 's'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button onClick={() => canPrev && setFy(fy - 1)} disabled={!canPrev}
            style={{ ...fyBtn, opacity: canPrev ? 1 : 0.4, cursor: canPrev ? 'pointer' : 'not-allowed' }} aria-label="Previous financial year">‹</button>
          <div style={{ flex: 1, textAlign: 'center', background: '#fff', borderRadius: 12, height: 44, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', lineHeight: 1.1 }}>
            <span style={{ fontSize: 15, fontWeight: 800, color: NAVY }}>{data?.label || '—'}</span>
            <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.5, color: TEAL }}>1 JUL – 30 JUN</span>
          </div>
          <button onClick={() => canNext && setFy(fy + 1)} disabled={!canNext}
            style={{ ...fyBtn, opacity: canNext ? 1 : 0.4, cursor: canNext ? 'pointer' : 'not-allowed' }} aria-label="Next financial year">›</button>
        </div>

        <div style={{ display: 'flex', background: '#cbd9e2', borderRadius: 11, padding: 3, gap: 3 }}>
          {['All', 'Paid', 'Outstanding'].map(f => (
            <button key={f} onClick={() => { setFilter(f); setMonthsShown(MONTHS_PER_PAGE); }}
              style={{
                flex: 1, height: 34, border: 'none', borderRadius: 9, cursor: 'pointer',
                fontSize: 13, fontWeight: 700,
                background: filter === f ? '#fff' : 'transparent',
                color: filter === f ? NAVY : '#5b7382',
                boxShadow: filter === f ? '0 1px 3px rgba(0,0,0,.10)' : 'none',
              }}>{f}</button>
          ))}
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0 24px' }}>

        {error   && <div style={{ ...card, color: '#b91c1c', fontSize: 13 }}>{error}</div>}
        {loading && <div style={{ ...card, color: '#6b7280', fontSize: 13 }}>Loading…</div>}

        {data && !loading && (
          <>
            {/* Totals */}
            <div style={card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start' }}>
                <div>
                  <div style={lbl}>Paid this financial year</div>
                  <div style={{ ...num, fontSize: 32, fontWeight: 800, color: GREEN, letterSpacing: -1, marginTop: 2 }}>
                    {money(data.totals.paid)}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={lbl}>Awaiting payment</div>
                  <div style={{ ...num, fontSize: 20, fontWeight: 800, color: data.totals.outstanding > 0 ? RED : '#9ca3af', marginTop: 4 }}>
                    {money(data.totals.outstanding)}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', borderTop: '1px solid #eef1f3', marginTop: 12, paddingTop: 12 }}>
                {[
                  ['Weeks', String(data.totals.weeks)],
                  ['Avg / week', money(data.totals.avg_per_week)],
                  ['Last paid', fmtDay(data.totals.last_paid)],
                ].map(([k, v]) => (
                  <div key={k} style={{ flex: 1, minWidth: 0 }}>
                    <div style={lbl}>{k}</div>
                    <div style={{ ...num, fontSize: 16, fontWeight: 700, color: NAVY, marginTop: 2 }}>{v}</div>
                  </div>
                ))}
              </div>

              {!data.zoho.configured && (
                <div style={{ marginTop: 12, background: '#fef6da', color: '#8a6410', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, fontWeight: 600 }}>
                  Payments aren't connected yet, so nothing shows as paid.
                </div>
              )}
            </div>

            {/* Outstanding callout */}
            {outstandingWeeks.length > 0 && (
              <button
                onClick={() => setFilter('Outstanding')}
                style={{ ...card, border: 'none', borderLeft: '3px solid #F5C518', width: '100%', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', textAlign: 'left' }}
              >
                <HourglassMedium size={20} weight="fill" color="#a16207" style={{ flex: 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: NAVY }}>
                    {outstandingWeeks.length} week{outstandingWeeks.length === 1 ? '' : 's'} awaiting payment
                  </div>
                  <div style={{ fontSize: 11.5, color: '#6b7280', marginTop: 1 }}>
                    Oldest: week {outstandingWeeks[outstandingWeeks.length - 1].week_number}
                  </div>
                </div>
                <span style={{ ...num, fontSize: 16, fontWeight: 800, color: RED }}>
                  {money(data.totals.outstanding)}
                </span>
              </button>
            )}

            {/* Month groups */}
            {shown.length === 0 ? (
              <div style={{ ...card, color: '#9ca3af', fontSize: 13.5 }}>
                {weeks.length === 0
                  ? 'No payments yet. Your first invoice will appear here once Michel processes it.'
                  : `Nothing ${filter.toLowerCase()} in this financial year.`}
              </div>
            ) : visibleGroups.map(g => (
              <React.Fragment key={g.key}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px 2px' }}>
                  <span style={{ ...lbl, color: '#7d919e' }}>{g.key}</span>
                  <span style={{ flex: 1, height: 1, background: '#d5dce1' }} />
                  <span style={{ ...num, fontSize: 12, fontWeight: 700, color: '#5b7382' }}>
                    {money(g.rows.reduce((s, w) => s + w.earned, 0))}
                  </span>
                </div>

                {g.rows.map(w => {
                  const st = STATE[w.state] || STATE.not_invoiced;
                  const Icon = st.Icon;
                  return (
                    <button key={w.week_start} onClick={() => setSheet(w)}
                      style={{ ...card, border: 'none', width: '100%', display: 'flex', alignItems: 'center', gap: 13, cursor: 'pointer', textAlign: 'left' }}>
                      <div style={{ width: 38, height: 38, flex: 'none', borderRadius: 11, background: st.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={20} weight="fill" color={st.fg} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#1a1a2e' }}>
                          Week {w.week_number} · {weekRange(w.week_start, w.week_end)}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 10.5, fontWeight: 700, borderRadius: 999, padding: '2px 8px', background: st.bg, color: st.pillFg || st.fg }}>
                            {st.label}
                          </span>
                          <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#9ca3af' }}>{w.invoice_number}</span>
                        </div>
                      </div>

                      <div style={{ textAlign: 'right', flex: 'none' }}>
                        <div style={{ ...num, fontSize: 16, fontWeight: 800, color: NAVY }}>{money(w.earned)}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 1 }}>
                          {w.paid_on ? `Paid ${fmtDay(w.paid_on)}` : `Week ended ${fmtDay(w.week_end)}`}
                        </div>
                      </div>
                      <CaretRight size={16} color="#c3ccd2" style={{ flex: 'none' }} />
                    </button>
                  );
                })}
              </React.Fragment>
            ))}

            {groups.length > monthsShown && (
              <button onClick={() => setMonthsShown(m => m + MONTHS_PER_PAGE)}
                style={{ ...card, border: 'none', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, cursor: 'pointer', color: '#9ca3af', fontSize: 12, fontWeight: 600 }}>
                <ArrowClockwise size={15} /> Load earlier months
              </button>
            )}

            {/* Transfers we received but couldn't tie to a week — shown so the
                total on top always reconciles with what the rows add up to. */}
            {data.unmatched_payments?.length > 0 && (
              <div style={{ ...card, fontSize: 12.5, color: '#6b7280' }}>
                <div style={lbl}>Other payments received</div>
                {data.unmatched_payments.map(p => (
                  <div key={p.zoho_id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 6 }}>
                    <span>{fmtDay(p.date)} · {p.description}</span>
                    <span style={{ ...num, fontWeight: 700, color: NAVY }}>{money(p.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Action bar ────────────────────────────────────────────────── */}
      {data && (
        <div style={{ position: 'sticky', bottom: 0, background: '#fff', borderTop: '1px solid #e2e6e9', padding: '10px 16px', display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => window.print()}
            style={{ flex: 1, height: 48, borderRadius: 12, border: 'none', background: TEAL, color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <FilePdf size={20} weight="fill" /> Download statement PDF
          </button>
        </div>
      )}

      {/* ── Payment detail sheet ──────────────────────────────────────── */}
      {sheet && (
        <div onClick={() => setSheet(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 2000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 560, borderRadius: '18px 18px 0 0', padding: '10px 16px 20px' }}>
            <div style={{ width: 40, height: 4, background: '#e2e6e9', borderRadius: 2, margin: '0 auto 14px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={lbl}>{sheet.state === 'paid' ? 'Payment received' : 'Awaiting payment'}</div>
                <div style={{ ...num, fontSize: 30, fontWeight: 800, color: sheet.state === 'paid' ? GREEN : RED, marginTop: 2 }}>
                  {money(sheet.state === 'paid' ? sheet.paid : sheet.outstanding)}
                </div>
              </div>
              {sheet.paid_on && (
                <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '5px 12px', background: '#dcfce7', color: '#15803d', whiteSpace: 'nowrap' }}>
                  Paid {fmtDay(sheet.paid_on)}
                </span>
              )}
            </div>

            <div style={{ marginTop: 14 }}>
              {[
                ['Covers', `Week ${sheet.week_number} · ${weekRange(sheet.week_start, sheet.week_end)}`],
                ['Earned', money(sheet.earned)],
                ['Jobs', String(sheet.jobs)],
                ['Invoice', sheet.invoice_number],
                ...(sheet.payments || []).map(p => [`Paid ${fmtDay(p.date)}`, money(p.amount)]),
              ].map(([k, v], i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '11px 0', borderTop: i ? '1px solid #eef1f3' : 'none' }}>
                  <span style={{ fontSize: 13, color: '#6b7280' }}>{k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: NAVY, fontFamily: /INV-|BSB/.test(v) ? 'monospace' : undefined, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
            </div>

            {sheet.payments?.length > 0 && (
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                Bank reference: {sheet.payments[0].description}
              </div>
            )}

            <button onClick={() => setSheet(null)}
              style={{ width: '100%', height: 48, marginTop: 16, borderRadius: 12, border: 'none', background: '#f3f4f6', color: NAVY, fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
